// Elite static skills — all are hold-based. The shorter time is the beginner
// tier, the longer one is mastery. estHoldSeconds doubles as the unlock
// gate, per the progression engine. T-007 retrofitted every per-skill string
// to LocalizedText (de/hu/en) per ADR-0006. Quality pass applied in T-010.

import type { Exercise, LocalizedList, LocalizedText } from "../types/calisthenics";
import { EXERCISE_IDS } from "./__generated__/exerciseIds";

const L = (de: string, hu: string, en: string): LocalizedText => ({ de, hu, en });
const C = (de: string[], hu: string[], en: string[]): LocalizedList => ({ de, hu, en });

function tierFor(category: Exercise["category"]): Exercise["tier"] {
  switch (category) {
    case "pushup":
    case "squat":
    case "pullup":
    case "leg_raise":
    case "bridge":
    case "handstand_pushup":
      return "big6";
    default:
      return "elite";
  }
}

const hold = (
  category: Exercise["category"],
  stepNumber: Exercise["stepNumber"],
  name: LocalizedText,
  description: LocalizedText,
  cues: LocalizedList,
  beginnerHold: number,
  intermediateHold: number,
  masteryHold: number
): Exercise => {
  const slug = `${category}-${stepNumber}`;
  return {
    id: EXERCISE_IDS[slug],
    slug,
    name,
    category,
    tier: tierFor(category),
    stepNumber,
    description,
    cues,
    standard: {
      beginnerReps: 0,
      intermediateReps: 0,
      masteryReps: 0,
      holdSeconds: masteryHold,
      beginnerHoldSeconds: beginnerHold,
      intermediateHoldSeconds: intermediateHold,
    },
  };
};

export const humanFlag: Exercise[] = [
  hold("human_flag", 1,
    L("Tuck Human Flag", "Tuck zászló", "Tuck Human Flag"),
    L("Neben einer vertikalen Stange, der Körper angehockt, die Beine gebeugt nah an der Stange.", "A függőleges rúd mellett, a test összehúzva, a térdek a mellkashoz húzva.", "Next to a vertical pole, body tucked, legs parallel to the bar."),
    C(["Oberer Arm zieht kraftvoll, unterer Arm drückt und stützt", "Bauch maximal anspannen, Hüfte nah an der Stange", "Schultern aktiv stabilisieren, nicht einsinken"],
      ["Felső kéz húz, alsó kéz határozottan támaszt és tol", "Feszítsd a törzsed, a csípő a rúdhoz közel marad", "Aktív vállak, ne engedd ki a stabilitást"],
      ["Top grip: overhand, bottom: hook", "Brace the core, hips close to the bar", "Shoulder active, do not sag"]),
    5, 10, 15),
  hold("human_flag", 2,
    L("Halbe Human Flag", "Fél zászló", "Half Human Flag"),
    L("Der Oberkörper ist waagerecht geöffnet, die Beine bleiben noch gebeugt oder gegrätscht.", "A felsőtest már vízszintesre nyílik, de a lábak még behajlítva maradnak.", "The body is open but the legs stay parallel to the bar, hips close to the bar."),
    C(["Hüfte in einer Linie mit den Schultern halten", "Obere Schulter aktiv geöffnet halten", "Haltezeit mit sauberer Spannung aufbauen"],
      ["Tartsd a csípőt a vállakkal egy vonalban", "A felső váll nyitott és aktív", "Lassan, kontrollált feszítéssel építsd az időt"],
      ["Hips stay at the bar", "Build hold time slowly", "Opposite shoulder stays open"]),
    5, 8, 12),
  hold("human_flag", 3,
    L("Volle Human Flag", "Teljes emberi zászló", "Full Human Flag"),
    L("Der gesamte Körper bildet eine vollkommen gerade, waagerechte Linie zur Stange — die ultimative Hebelkraft.", "A test teljesen egyenes, vízszintes vonalat alkot a rúd mellett — a laterális törzserő csúcsa.", "The body fully horizontal, legs away from the bar — the goal."),
    C(["Körper bildet eine gerade waagerechte Linie", "Oberer Arm zieht, unterer Arm drückt wie eine Säule", "Gesäß und seitliche Bauchmuskeln maximal spannen"],
      ["A test egyetlen egyenes, vízszintes vonal", "A felső kar húz, az alsó sziklaszilárdan támaszt", "Farizom és oldalsó hasfal maximálisan feszes"],
      ["Body in a straight horizontal line", "Holding arm's shoulder down, support arm up", "Slow, even"]),
    3, 5, 10),
];

export const planche: Exercise[] = [
  hold("planche", 1,
    L("Frog Stand (Froschstand)", "Békatartás (Frog Stand)", "Frog Planche"),
    L("Auf den Händen balancierend, Knie auf den Ellbogen oder dem Trizeps abgestützt — Vorbereitung für Handgelenke und Schultern.", "A kezeken támaszkodva, a térdek a könyökökön vagy a tricepszen pihennek — a csukló és a váll előkészítése.", "Balanced on the hands, knees on the triceps — shoulder and wrist preparation."),
    C(["Finger leicht spreizen, Handgelenke unter die Schultern", "Schultern leicht nach vorne lehnen (Lean)", "Blick nach vorne unten auf den Boden richten"],
      ["Ujjak szétterpesztve, stabil támasz a talajon", "A vállak finoman előredőlnek a csukló elé", "Tekintet a kezek elé a talajra szegeződik"],
      ["Fingers spread, wrist under the shoulder", "Pull the knees to the armpits", "Shoulder forward, hips high"]),
    10, 20, 30),
  hold("planche", 2,
    L("Tuck Planche", "Tuck Planche", "Tuck Planche"),
    L("Frei in der Luft auf den Händen gestützt, Knie eng an die Brust gezogen, Arme vollständig gestreckt.", "A levegőben, a térdek a mellkashoz húzva, a karok teljesen nyújtva — az igazi váll- és csuklóerő próbatétele.", "In the air, knees at the chest, arms extended — the fire test for wrist and shoulder."),
    C(["Arme komplett durchgestreckt, Ellbogen blockiert", "Schulterblätter aktiv auseinanderdrücken (Protraktion)", "Becken nach hinten kippen (PPT) und Rumpf felsenfest spannen"],
      ["Karok teljesen nyújtva, a könyökök zárolva", "Lapockák kitolva (protritzálás), hát felső része enyhén domború", "Medence hátrafelé billentve, hasfal maximálisan feszít"],
      ["Arms straight, do not bend", "Back rounded, pelvis tilted", "Look at the floor, in front of the hands"]),
    8, 15, 25),
  hold("planche", 3,
    L("Straddle Planche", "Straddle Planche", "Straddle Planche"),
    L("Beine weit gegrätscht und voll gestreckt, Körper waagerecht über dem Boden.", "Széles terpesztett lábakkal, vízszintesen lebegő testtel, nyújtott karokkal a talaj felett.", "Legs spread, arms straight — leading to the full planche."),
    C(["Beine maximal grätschen und Zehen strecken", "Schultern weit nach vorne lehnen, Hüfte auf Schulterhöhe", "Gesäß und unteren Rücken unter Dauerspannung halten"],
      ["Lábak maximális terpeszben, spicc feszít", "Vállak erősen előredőlnek, csípő vállmagasságban", "Farizom és alsó hát folyamatos maximális feszítésben"],
      ["Legs wide and even", "Shoulder forward, hips high", "Keep the core active"]),
    5, 10, 20),
  hold("planche", 4,
    L("Full Planche", "Teljes Planche", "Full Planche"),
    L("Der Körper bildet mit geschlossenen Beinen eine waagerechte Linie — die Königsklasse der statischen Drückkraft.", "A test összezárt lábakkal teljesen egyenes és vízszintes — a calisthenics statikus tolóerejének csúcsa.", "Body straight, legs together, arms straight — one of the hardest static elements."),
    C(["Körper in einer perfekten, waagerechten Linie", "Maximale Protraktion und Depression der Schulterblätter", "Arme unnachgiebig gestreckt, Handgelenke stabil"],
      ["A test tökéletesen egyenes és vízszintes vonalat alkot", "Lapockák maximálisan kitolva, vállak elöl", "Karok törhetetlenül nyújtva, zárt lábakkal spicc"],
      ["Body in a straight line", "Shoulder in front of the hand, hips high", "Arms straight, strong wrist"]),
    3, 5, 10),
];

export const frontLever: Exercise[] = [
  hold("front_lever", 1,
    L("Tuck Front Lever", "Tuck Front Lever", "Tuck Front Lever"),
    L("An der Stange hängend, Knie an die Brust gezogen, Oberkörper waagerecht — das Fundament für Latissimus und Griffkraft.", "A rúdon lógva, a térdek a mellkashoz zárva, a törzs vízszintes — a széles hátizom és a fogóerő alapköve.", "Hanging on the bar, knees at the chest, body horizontal — base for back and grip."),
    C(["Schultern aktiv nach hinten-unten fixieren (Depression)", "Arme voll durchgestreckt halten", "Brust herausdrücken, Rücken flach halten"],
      ["Aktív vállak, lapockák határozottan lefelé húzva", "Karok teljesen nyújtva", "Mellkas kiemelve, a hát egyenes és feszes"],
      ["Active shoulder, scapula down", "Arms straight", "Back straight"]),
    10, 20, 30),
  hold("front_lever", 2,
    L("Advanced Tuck Front Lever", "Advanced Tuck Front Lever", "Advanced Tuck Front Lever"),
    L("Der Rücken bleibt flach, die Oberschenkel öffnen sich in einen 90°-Winkel zum Oberkörper.", "A hát teljesen lapos, a combok 90 fokos szöget zárnak be a vízszintes törzzsel.", "Back straight, hips at a right angle, knees in front of the belly."),
    C(["Rücken vollkommen flach halten", "Hüfte auf Höhe der Stange stabilisieren", "Stange aktiv nach unten drücken mit gestreckten Armen"],
      ["A hát teljesen lapos és vízszintes marad", "A csípő a rúd magasságában tartva", "A rudat aktívan nyomd lefelé nyújtott karokkal"],
      ["Back flat", "Hips level with the bar", "Arms straight, do not bend"]),
    8, 15, 25),
  hold("front_lever", 3,
    L("Straddle Front Lever", "Straddle Front Lever", "Straddle Front Lever"),
    L("Die Beine weit gegrätscht und gestreckt, der gesamte Körper in einer waagerechten Ebene.", "Széles terpeszben nyújtott lábakkal, a teljes test vízszintes síkban a talajjal párhuzamosan.", "Legs spread, body almost horizontal, arms straight."),
    C(["Beine maximal grätschen und Zehen anspannen", "Latissimus maximal anspannen, Hüfte nicht durchhängen lassen", "Kopf neutral in Verlängerung der Wirbelsäule"],
      ["Lábak széles terpeszben, spiccelve", "Széles hátizom és farizom kőkeményen feszít", "A fej semleges, a gerinc meghosszabbításában"],
      ["Legs wide and even", "Pelvis slightly high", "Back straight, do not curve it"]),
    5, 10, 20),
  hold("front_lever", 4,
    L("Full Front Lever", "Teljes Front Lever", "Full Front Lever"),
    L("Der Körper bildet eine einzige waagerechte Linie von Kopf bis Fuß — die Krönung der Zugarbeit.", "A test fejtől a lábujjak hegyéig teljesen egyenes és vízszintes — a húzóerő mesterfoka.", "Body fully horizontal, legs together — the pinnacle of pulling."),
    C(["Körper in einer perfekten waagerechten Linie halten", "Schulterblätter fest nach unten und hinten verankert", "Arme unnachgiebig gestreckt, volle Körperspannung"],
      ["A test tökéletesen vízszintes egyenes vonalat alkot", "Lapockák határozottan zárva és lefelé húzva", "Karok feszítve nyújtva, teljes test feszesség"],
      ["Body straight and horizontal", "Shoulder active, back flat", "Hold steady, do not shake"]),
    3, 5, 10),
];

export const muscleUp: Exercise[] = [
  hold("muscle_up", 1,
    L("Negativer Muscle-Up", "Negatív Muscle-Up", "Negative Muscle-Up"),
    L("Im Stütz über der Stange starten und die Übergangsphase langsam und kontrolliert nach unten absenken.", "A rúd feletti támaszból indítva lassan, fékezve engeded le magad az átfordulási ponton keresztül.", "Standing above the bar, sink under the bar with control — the strength foundation."),
    C(["Griff: Fester Ristgriff oder False Grip", "Ellbogen eng am Körper führen", "Den Übergang langsam über mindestens 3 Sekunden kontrollieren"],
      ["Fogás: stabil madárfogás vagy mély fogás (false grip)", "A könyököket szorosan a test mellett vezesd", "Az átfordulási fázist lassan, legalább 3 másodpercig fékezd"],
      ["Grip: palms toward you", "Elbows close to the body", "Slow, three seconds down"]),
    3, 5, 8),
  hold("muscle_up", 2,
    L("Assistierter Muscle-Up", "Asszisztált Muscle-Up", "Assisted Muscle-Up"),
    L("Mit Unterstützung durch ein Widerstandsband den vollen Übergang aus dem Hang in den Stütz erlernen.", "Gumiszalag vagy partner segítségével végzett teljes átfordulás a függésből a támaszba.", "Full transition with a resistance band or partner assistance."),
    C(["Explosiv nach oben ziehen, Brust über die Stange bringen", "Beide Ellbogen gleichzeitig über die Stange drehen", "Saubere Bewegung ohne asymmetrisches Nachziehen"],
      ["Robbanékony húzás mellkashoz, gyors átfordulás", "Mindkét könyök egyszerre forduljon át a rúd fölé", "Kerüld az egykaros féloldalas átbillenést"],
      ["No swing, pure strength", "Elbows close to the body", "Wrist stays on the bar"]),
    1, 3, 5),
  hold("muscle_up", 3,
    L("Sauberer Muscle-Up", "Teljes Muscle-Up", "Full Muscle-Up"),
    L("Aus dem toten Hang explosiv nach oben ziehen, über die Stange umgreifen und in den Stütz drücken.", "Holtpontos függésből robbanékony húzás, szimmetrikus átfordulás és kitolás a rúd fölé.", "Below the bar, above the bar, arms straight — the marriage of pull and press."),
    C(["Explosiver Zug zur Brust ohne übermäßigen Beinschwung", "Beide Ellbogen drehen synchron und sauber über die Stange", "Oben in den vollen Stütz mit gestreckten Armen auslocken"],
      ["Robbanékony húzás minimális láblendülettel", "Mindkét könyök szinkronban fordul át a rúd fölé", "A rúd felett határozott, tiszta kitolás nyújtott karokig"],
      ["No swing, pure strength", "Elbows forward, wrist turns", "At the top: arms straight"]),
    1, 2, 3),
];

export const lsit: Exercise[] = [
  hold("l_sit", 1,
    L("Tuck L-Sit", "Tuck L-ülés", "Tuck L-sit"),
    L("Auf Holmen oder dem Boden aufgestützt, die Knie eng an die Brust gezogen, der Körper frei in der Luft.", "Talajon vagy korláton támaszkodva, térdek a mellkashoz húzva, a test a talajtól elemelve.", "Beside the hands, knees at the chest, body lifted off the floor."),
    C(["Arme senkrecht und vollkommen gestreckt", "Schultern aktiv nach unten drücken (Depression)", "Knie eng an den Oberkörper ziehen"],
      ["Karok teljesen nyújtva, zárolt könyökökkel", "Vállak aktívan lefelé tolva a fülektől távol", "Térdek szorosan a mellkashoz zárva"],
      ["Arms vertical", "Scapulae down and retracted", "Legs in the air"]),
    10, 20, 30),
  hold("l_sit", 2,
    L("L-Sit", "L-ülés", "L-sit (Straight legs)"),
    L("Beine vollkommen waagerecht nach vorne gestreckt im 90°-Winkel zum Rumpf — absolute Kernkraft.", "A lábak nyújtva, a talajjal párhuzamosan, 90 fokos derékszögben a törzzsel.", "Legs horizontal, at a right angle to the body — fire test for abs and hip flexors."),
    C(["Beine komplett durchstrecken, Zehenspitzen nach vorne", "Schultern aktiv nach unten drücken, Brust stolz", "Bauch und Quadrizeps maximal anspannen"],
      ["Lábak teljesen kinyújtva, spiccelve a talajjal párhuzamosan", "Vállak aktívan lent, a mellkas büszkén kiemelve", "Hasfal és combizmok kőkeményen feszítve"],
      ["Back rounded, core active", "Legs parallel to the floor", "Arms straight"]),
    5, 10, 20),
  hold("l_sit", 3,
    L("V-Sit", "V-ülés", "V-sit"),
    L("Die Beine weit über die Waagerechte nach oben gezogen, der Körper bildet ein scharfes V.", "A lábak magasan a vízszintes fölé emelve, a test éles V alakot formál.", "Legs up, body forming a V — total core press and flexibility peak."),
    C(["Beine gestreckt Richtung Kopf ziehen", "Maximale Hüftbeuger- und Rumpfkraft aufbringen", "Schultergürtel aktiv nach unten stemmen"],
      ["Lábak feszesen nyújtva a fej irányába emelve", "Maximális csípőhajlító és hasizom feszítés", "Vállöv határozottan lefelé támasztva"],
      ["Legs straight and up", "Back rounded", "Core active, pelvis tilted"]),
    3, 5, 10),
];

export const eliteSkills: Exercise[] = [
  ...humanFlag,
  ...planche,
  ...frontLever,
  ...muscleUp,
  ...lsit,
];

export const eliteSkillsById: Record<string, Exercise> = Object.fromEntries(
  eliteSkills.map((e) => [e.id, e])
);

export const eliteChainsByCategory: Exercise[][] = [
  humanFlag,
  planche,
  frontLever,
  muscleUp,
  lsit,
];
